import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';
import styles from './CartPage.module.css';
import sharedStyles from '../styles/SharedComponents.module.css';
import Logo from '../components/Logo';

export default function CartPage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, setCart] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    specialInstructions: ''
  });

  // Get table number from URL parameter first, then location state
  const urlTableParam = new URLSearchParams(location.search).get('table');
  const tableNumber = urlTableParam || location.state?.tableNumber || null;

  // This effect loads the restaurant and cart data on component mount
  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('*')
          .eq('id', restaurantId)
          .single();

        if (error) throw error;
        setRestaurant(data);
        
        // Load cart from localStorage
        const savedCart = localStorage.getItem(`cart-${restaurantId}`);
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          setCart(parsedCart);
          
          // Only load customer info if cart has items
          if (parsedCart.length > 0) {
            const savedCustomerInfo = localStorage.getItem(`customer-info-${restaurantId}`);
            if (savedCustomerInfo) {
              setCustomerInfo(JSON.parse(savedCustomerInfo));
            }
          } else {
            // Clear customer info for empty carts
            localStorage.removeItem(`customer-info-${restaurantId}`);
          }
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurant();
  }, [restaurantId]);

  const removeItem = (itemId) => {
    const updatedCart = cart.filter(item => item.id !== itemId);
    setCart(updatedCart);
    localStorage.setItem(`cart-${restaurantId}`, JSON.stringify(updatedCart));
    
    // If cart becomes empty, also clear customer info
    if (updatedCart.length === 0) {
      localStorage.removeItem(`customer-info-${restaurantId}`);
      setCustomerInfo({
        name: '',
        email: '',
        specialInstructions: ''
      });
    }
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedCart = cart.map(item => 
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    
    setCart(updatedCart);
    localStorage.setItem(`cart-${restaurantId}`, JSON.stringify(updatedCart));
  };

  // Update special instructions for a specific item
  const updateItemInstructions = (itemId, instructions) => {
    const updatedCart = cart.map(item => 
      item.id === itemId ? { ...item, specialInstructions: instructions } : item
    );
    
    setCart(updatedCart);
    localStorage.setItem(`cart-${restaurantId}`, JSON.stringify(updatedCart));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  // Clear the entire cart
  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(`cart-${restaurantId}`);
    localStorage.removeItem(`customer-info-${restaurantId}`);
    setCustomerInfo({
      name: '',
      email: '',
      specialInstructions: ''
    });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;
    
    setSubmitting(true);
    
    // Save customer info to localStorage only if the order is proceeding
    localStorage.setItem(`customer-info-${restaurantId}`, JSON.stringify(customerInfo));
    
    try {
      console.log("Cart item example:", cart[0]); // Log the structure of an item to verify it has name
      
      console.log("Creating order with data:", {
        restaurant_id: restaurantId,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        special_instructions: customerInfo.specialInstructions,
        table_number: tableNumber,
        total_amount: calculateSubtotal(),
        items: cart
      });
      
      // Send the order to the server endpoint instead of direct Supabase access
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          restaurant_id: restaurantId,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email || null,
          special_instructions: customerInfo.specialInstructions || null,
          table_number: tableNumber || null,
          total_amount: calculateSubtotal(),
          items: cart
        }),
      });
      
      // Log full response for debugging
      console.log("Server response status:", response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error response data:", errorData);
        throw new Error(errorData.error || 'Failed to create order');
      }
      
      const orderData = await response.json();
      console.log("Order created successfully:", orderData);
      
      // Handle successful order creation
      handleOrderSuccess(orderData);
    } catch (error) {
      console.error('Error creating order:', error);
      alert(`There was a problem creating your order: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle successful order creation
  const handleOrderSuccess = (orderData) => {
    // Clear cart and customer info after successful order creation
    localStorage.removeItem(`cart-${restaurantId}`);
    localStorage.removeItem(`customer-info-${restaurantId}`);
    
    // Navigate to checkout with the orderId and table parameter if available
    if (orderData && orderData.id) {
      console.log(`Navigating to checkout with order ID: ${orderData.id}`);
      navigate(`/checkout/${orderData.id}${tableNumber ? `?table=${tableNumber}` : ''}`);
    }
  };

  if (loading) {
    return (
      <div className={sharedStyles.pageContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={sharedStyles.pageContainer}>
      <div className={styles.banner}>
        <h2>Your Order from {restaurant.name}</h2>
        {tableNumber && (
          <div className={styles.tableInfo}>Table: {tableNumber}</div>
        )}
      </div>
      
      <div className={styles.content}>
        <Link to={`/menu/${restaurantId}${tableNumber ? `?table=${tableNumber}` : ''}`} className={styles.returnLink}>
          ← Back to Menu
        </Link>
        
        <div className={styles.cartSection}>
          <div className={styles.sectionHeader}>
            <h3>Order Items</h3>
            {cart.length > 0 && (
              <button onClick={clearCart} className={styles.clearCartButton}>
                Clear Cart
              </button>
            )}
          </div>
          
          {cart.length === 0 ? (
            <div className={styles.emptyCart}>
              <p>Your cart is empty</p>
              <Link to={`/menu/${restaurantId}${tableNumber ? `?table=${tableNumber}` : ''}`} className={styles.browseMenuLink}>
                Browse the menu
              </Link>
            </div>
          ) : (
            <div className={styles.itemsList}>
              {cart.map(item => (
                <div key={item.id} className={styles.cartItem}>
                  <div className={styles.itemDetails}>
                    <h4 className={styles.itemName}>{item.name}</h4>
                    <div className={styles.itemPrice}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                  
                  <div className={styles.specialInstructionsContainer}>
                    <textarea
                      className={styles.specialInstructionsInput}
                      placeholder="Special instructions for this item..."
                      value={item.specialInstructions || ""}
                      onChange={(e) => updateItemInstructions(item.id, e.target.value)}
                    />
                  </div>
                  
                  <div className={styles.itemControls}>
                    <div className={styles.quantityControl}>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className={styles.quantityButton}
                      >
                        -
                      </button>
                      <span className={styles.quantity}>{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className={styles.quantityButton}
                      >
                        +
                      </button>
                    </div>
                    
                    <button 
                      onClick={() => removeItem(item.id)}
                      className={styles.removeButton}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              
              <div className={styles.subtotal}>
                <div>Subtotal:</div>
                <div>${calculateSubtotal().toFixed(2)}</div>
              </div>
            </div>
          )}
        </div>
        
        <form onSubmit={handleCheckout} className={styles.checkoutForm}>
          <div className={styles.sectionHeader}>
            <h3>Customer Information</h3>
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              name="name"
              value={customerInfo.name}
              onChange={handleInputChange}
              required
              className={styles.input}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="email">Email (optional)</label>
            <input
              id="email"
              type="email"
              name="email"
              value={customerInfo.email}
              onChange={handleInputChange}
              className={styles.input}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="specialInstructions">Special Instructions for Entire Order (optional)</label>
            <textarea
              id="specialInstructions"
              name="specialInstructions"
              value={customerInfo.specialInstructions}
              onChange={handleInputChange}
              className={styles.textarea}
            />
          </div>
          
          <button 
            type="submit" 
            disabled={cart.length === 0 || !customerInfo.name || submitting}
            className={styles.primaryButton}
          >
            {submitting ? 'Processing...' : 'Proceed to Payment'}
          </button>
        </form>
      </div>
    </div>
  );
}